import React, { ReactElement, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Analytics from '@mui/icons-material/Analytics';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Description from '@mui/icons-material/Description';
import PeopleAlt from '@mui/icons-material/PeopleAlt';
import Publish from '@mui/icons-material/Publish';
import Refresh from '@mui/icons-material/Refresh';
import { useService } from '../contexts/ServiceContext';
import ResponsiveLayout from '../components/common/ResponsiveLayout';
import { DashboardData } from '../system/analytics';

const Dashboard: React.FC = () => {
  const { getAnalyticsDashboard } = useService();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [timeframe, setTimeframe] = useState('6');
  const [selectedView, setSelectedView] = useState<string | null>(null);

  const fetchDashboardData = async (refresh: boolean = false) => {
    setLoading(true);
    setError(null);

    try {
      const response = await getAnalyticsDashboard(timeframe, refresh);
      setData(response);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [timeframe]);

  if (loading && !data) {
    return (
      <ResponsiveLayout>
        <CircularProgress />
      </ResponsiveLayout>
    );
  }

  if (error) {
    return (
      <ResponsiveLayout>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            Error loading dashboard
          </Typography>
          <Typography>{error}</Typography>
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => fetchDashboardData()}
          >
            Try Again
          </Button>
        </Box>
      </ResponsiveLayout>
    );
  }

  const handleRefresh = () => {
    fetchDashboardData(true);
  };

  const handleTimeframeChange = (event: SelectChangeEvent) => {
    setTimeframe(event.target.value);
  };

  const handleBack = () => {
    setSelectedView(null);
  };

  const renderDetailView = () => {
    if (!selectedView || !data) return null;

    const detailMap: Record<
      string,
      { title: string; data: any[]; icon: ReactElement }
    > = {
      users: {
        title: 'Unique Users',
        data: data.details.users,
        icon: <PeopleAlt fontSize="large" color="primary" />,
      },
      createdListings: {
        title: 'Created Listings',
        data: data.details.createdListings,
        icon: <Description fontSize="large" color="primary" />,
      },
      publishedListings: {
        title: 'Published Listings',
        data: data.details.publishedListings,
        icon: <Publish fontSize="large" color="primary" />,
      },
    };

    const detail = detailMap[selectedView];

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            sx={{ mr: 2 }}
            startIcon={<ArrowBack />}
          >
            Back
          </Button>
          <Typography
            variant="h4"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            {detail.icon}
            <Box sx={{ ml: 1 }}>{detail.title} Details</Box>
          </Typography>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>User ID</TableCell>
                <TableCell>Email</TableCell>
                {selectedView === 'publishedListings' && (
                  <TableCell>Listing Key</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {(selectedView === 'users'
                ? detail.data.map((item) => ({
                    timestamp: item.lastActive,
                    userId: item.id,
                    userEmail: item.email,
                  }))
                : detail.data
              ).map((item, index) => (
                <TableRow
                  key={index}
                  sx={{
                    bgcolor: index % 2 === 0 ? 'grey.50' : 'background.paper',
                    '&:hover': { bgcolor: 'grey.100' },
                  }}
                >
                  <TableCell>{item.timestamp}</TableCell>
                  <TableCell>{item.userId}</TableCell>
                  <TableCell>{item.userEmail}</TableCell>
                  {selectedView === 'publishedListings' && (
                    <TableCell>{item.listingKey}</TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderSummaryView = () => {
    if (!data) return null;

    return (
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography
            variant="h4"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Analytics fontSize="large" sx={{ mr: 1 }} />
            NextGen Analytics Dashboard
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControl
              variant="outlined"
              size="small"
              sx={{ minWidth: 120, mr: 2 }}
            >
              <InputLabel>Time Period</InputLabel>
              <Select
                value={timeframe}
                onChange={handleTimeframeChange}
                label="Time Period"
              >
                <MenuItem value="1">1 Month</MenuItem>
                <MenuItem value="3">3 Months</MenuItem>
                <MenuItem value="6">6 Months</MenuItem>
                <MenuItem value="12">12 Months</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Data from {new Date(data.timeframe.start).toLocaleDateString()} to{' '}
            {new Date(data.timeframe.end).toLocaleDateString()}
            {data.metadata.cached ? ' (Cached)' : ' (Fresh)'}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              sx={{
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
              onClick={() => setSelectedView('users')}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Unique Users
                    </Typography>
                    <Typography variant="h3">
                      {data.summary.uniqueUserCount}
                    </Typography>
                  </Box>
                  <PeopleAlt fontSize="large" color="primary" />
                </Box>
                <Typography variant="body2" sx={{ mt: 2, fontSize: '0.8rem' }}>
                  Click to view user details
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              sx={{
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
              onClick={() => setSelectedView('createdListings')}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Created Listings
                    </Typography>
                    <Typography variant="h3">
                      {data.summary.createdListingsCount}
                    </Typography>
                  </Box>
                  <Description fontSize="large" color="primary" />
                </Box>
                <Typography variant="body2" sx={{ mt: 2, fontSize: '0.8rem' }}>
                  Click to view creation details
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              sx={{
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
              onClick={() => setSelectedView('publishedListings')}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Published Listings
                    </Typography>
                    <Typography variant="h3">
                      {data.summary.publishedListingsCount}
                    </Typography>
                  </Box>
                  <Publish fontSize="large" color="primary" />
                </Box>
                <Typography variant="body2" sx={{ mt: 2, fontSize: '0.8rem' }}>
                  Click to view publication details
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <TableContainer sx={{ maxHeight: 300 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    ...data.details.publishedListings,
                    ...data.details.createdListings,
                  ]
                    .sort(
                      (a, b) =>
                        new Date(b.timestamp).getTime() -
                        new Date(a.timestamp).getTime(),
                    )
                    .slice(0, 10)
                    .map((item, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          bgcolor:
                            index % 2 === 0 ? 'grey.50' : 'background.paper',
                          '&:hover': { bgcolor: 'grey.100' },
                        }}
                      >
                        <TableCell>{item.timestamp}</TableCell>
                        <TableCell>{item.userEmail}</TableCell>
                        <TableCell>
                          {item.listingKey
                            ? `Published listing ${item.listingKey}`
                            : 'Created listing'}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {loading && (
        <Box
          sx={{ position: 'fixed', top: 0, left: 0, right: 0, height: '4px' }}
        >
          <LinearProgress />
        </Box>
      )}

      {selectedView ? renderDetailView() : renderSummaryView()}
    </Box>
  );
};

export default Dashboard;
